import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
    Shirt, ArrowDownCircle, ArrowUpCircle, Search, AlertTriangle,
    CheckCircle2, Scissors, Package, Info
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
    Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const initialEntradaForm = {
    fecha: new Date().toISOString().split('T')[0],
    hora: new Date().toTimeString().slice(0, 5),
    proveedor_id: '', tipo_tela: '', color: '',
    cantidad_metros: 0, costo_total: 0, lote_id: '',
    entrega_nombre: '', recibe_nombre: '', observaciones: ''
};

const initialSalidaForm = {
    fecha: new Date().toISOString().split('T')[0],
    hora: new Date().toTimeString().slice(0, 5),
    corte_id: '', tipo_tela: '', color: '',
    cantidad_cortes: '', promedio_metros_por_corte: '',
    entrega_nombre: '', recibe_nombre: '', observaciones: ''
};

export const Telas = () => {
    const [registros, setRegistros] = useState([]);
    const [inventario, setInventario] = useState([]);
    const [inventarioDetalle, setInventarioDetalle] = useState([]);
    const [proveedores, setProveedores] = useState([]);
    const [lotes, setLotes] = useState([]);
    const [cortes, setCortes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [entradaModalOpen, setEntradaModalOpen] = useState(false);
    const [salidaModalOpen, setSalidaModalOpen] = useState(false);
    const [entradaForm, setEntradaForm] = useState(initialEntradaForm);
    const [salidaForm, setSalidaForm] = useState(initialSalidaForm);
    const [confirmando, setConfirmando] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            const [telasRes, invRes, invDetRes, provRes, lotesRes, cortesRes] = await Promise.all([
                axios.get(`${API_URL}/api/telas`),
                axios.get(`${API_URL}/api/telas/inventario`),
                axios.get(`${API_URL}/api/telas/inventario-detalle`),
                axios.get(`${API_URL}/api/proveedores`),
                axios.get(`${API_URL}/api/lotes`),
                axios.get(`${API_URL}/api/cortes`)
            ]);
            setRegistros(telasRes.data);
            setInventario(invRes.data);
            setInventarioDetalle(invDetRes.data);
            setProveedores(provRes.data);
            setLotes(lotesRes.data);
            setCortes(cortesRes.data);
        } catch (error) {
            console.error('Error:', error);
            toast.error('Error al cargar datos');
        } finally { setLoading(false); }
    };

    const handleEntradaSubmit = async (e) => {
        e.preventDefault();
        if (!entradaForm.proveedor_id || !entradaForm.tipo_tela || !entradaForm.cantidad_metros) {
            toast.error('Complete los campos requeridos'); return;
        }
        try {
            await axios.post(`${API_URL}/api/telas/entrada`, entradaForm);
            toast.success('Entrada registrada');
            setEntradaModalOpen(false);
            setEntradaForm(initialEntradaForm);
            fetchData();
        } catch (error) { toast.error('Error al registrar entrada'); }
    };

    // ── Salida: calculated fields ──
    const metrosSalida = useMemo(() => {
        const cant = parseFloat(salidaForm.cantidad_cortes) || 0;
        const prom = parseFloat(salidaForm.promedio_metros_por_corte) || 0;
        return cant * prom;
    }, [salidaForm.cantidad_cortes, salidaForm.promedio_metros_por_corte]);

    const stockSeleccionado = useMemo(() => {
        if (!salidaForm.tipo_tela || !salidaForm.color) return null;
        const item = inventarioDetalle.find(i => i.tipo_tela === salidaForm.tipo_tela && i.color === salidaForm.color);
        return item ? item.stock : 0;
    }, [salidaForm.tipo_tela, salidaForm.color, inventarioDetalle]);

    const stockDespues = stockSeleccionado !== null ? stockSeleccionado - metrosSalida : null;
    const stockSuficiente = stockDespues !== null && stockDespues >= 0;

    // Unique tipo_tela values with stock > 0
    const tiposTelaDisponibles = useMemo(() => {
        const set = new Set(inventarioDetalle.filter(i => i.stock > 0).map(i => i.tipo_tela));
        return [...set].sort();
    }, [inventarioDetalle]);

    // Colors available for selected tipo_tela
    const coloresDisponibles = useMemo(() => {
        if (!salidaForm.tipo_tela) return [];
        return inventarioDetalle
            .filter(i => i.tipo_tela === salidaForm.tipo_tela && i.stock > 0)
            .map(i => ({ color: i.color, stock: i.stock }));
    }, [salidaForm.tipo_tela, inventarioDetalle]);

    const corteSeleccionado = useMemo(() => {
        return cortes.find(c => c.id === salidaForm.corte_id);
    }, [salidaForm.corte_id, cortes]);

    const handleSalidaSubmit = async () => {
        if (!salidaForm.corte_id || !salidaForm.tipo_tela || !salidaForm.color || !salidaForm.cantidad_cortes || !salidaForm.promedio_metros_por_corte) {
            toast.error('Complete todos los campos requeridos'); return;
        }
        if (!stockSuficiente) {
            toast.error('Stock insuficiente para esta salida'); return;
        }
        if (!salidaForm.entrega_nombre || !salidaForm.recibe_nombre) {
            toast.error('Indique quien entrega y quien recibe'); return;
        }
        try {
            await axios.post(`${API_URL}/api/telas/salida-produccion`, {
                ...salidaForm,
                cantidad_cortes: parseInt(salidaForm.cantidad_cortes),
                promedio_metros_por_corte: parseFloat(salidaForm.promedio_metros_por_corte)
            });
            toast.success('Salida registrada correctamente');
            setSalidaModalOpen(false);
            setSalidaForm(initialSalidaForm);
            setConfirmando(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al registrar salida');
            setConfirmando(false);
        }
    };

    const getLoteNumero = (id) => lotes.find(l => l.id === id)?.numero_lote || '-';
    const getProveedorNombre = (id) => proveedores.find(p => p.id === id)?.nombre || '-';

    const entradas = registros.filter(r => r.tipo === 'entrada');
    const salidas = registros.filter(r => r.tipo === 'salida');

    const filteredEntradas = entradas.filter(r =>
        (r.color || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.tipo_tela || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    const filteredSalidas = salidas.filter(r =>
        (r.color || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.corte_relacion || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.tipo_tela || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div></div>;

    return (
        <div className="space-y-6 animate-fadeIn" data-testid="telas-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-zinc-100">Inventario de Telas</h1>
                    <p className="text-zinc-400 mt-1">Control de entradas y salidas de tela</p>
                </div>
                <div className="flex gap-2">
                    <Button onClick={() => setEntradaModalOpen(true)} className="bg-emerald-500 hover:bg-emerald-600" data-testid="add-entrada-tela-btn">
                        <ArrowDownCircle className="w-5 h-5 mr-2" /> Entrada
                    </Button>
                    <Button onClick={() => { setSalidaForm(initialSalidaForm); setConfirmando(false); setSalidaModalOpen(true); }} className="bg-red-500 hover:bg-red-600" data-testid="add-salida-tela-btn">
                        <ArrowUpCircle className="w-5 h-5 mr-2" /> Salida
                    </Button>
                </div>
            </div>

            {/* Inventory Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {inventario.filter(i => i.cantidad > 0).length > 0 ? inventario.filter(i => i.cantidad > 0).map((item, idx) => (
                    <Card key={idx} className="card">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-zinc-300">{item.tipo_tela}</p>
                                    <p className="text-xs text-zinc-500">{item.color}</p>
                                    <p className="text-2xl font-bold text-zinc-100 mt-1">{item.cantidad.toFixed(1)}m</p>
                                </div>
                                <div className="w-10 h-10 rounded-lg bg-amber-400/10 flex items-center justify-center">
                                    <Shirt className="w-5 h-5 text-amber-400" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )) : (
                    <Card className="card col-span-full">
                        <CardContent className="p-8 text-center text-zinc-400">No hay inventario de telas registrado</CardContent>
                    </Card>
                )}
            </div>

            {/* Search */}
            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input placeholder="Buscar por color, tipo o relacion de corte..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" />
            </div>

            {/* Tabs */}
            <Tabs defaultValue="entradas" className="w-full">
                <TabsList className="mb-4">
                    <TabsTrigger value="entradas"><ArrowDownCircle className="w-4 h-4 mr-2" /> Entradas ({entradas.length})</TabsTrigger>
                    <TabsTrigger value="salidas"><ArrowUpCircle className="w-4 h-4 mr-2" /> Salidas ({salidas.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="entradas">
                    <Card className="card"><CardContent className="p-0"><div className="overflow-x-auto">
                        <Table><TableHeader><TableRow className="bg-zinc-800">
                            <TableHead className="table-header">Fecha</TableHead>
                            <TableHead className="table-header">Proveedor</TableHead>
                            <TableHead className="table-header">Tipo Tela</TableHead>
                            <TableHead className="table-header">Color</TableHead>
                            <TableHead className="table-header">Cantidad</TableHead>
                            <TableHead className="table-header">Entrega</TableHead>
                            <TableHead className="table-header">Recibe</TableHead>
                        </TableRow></TableHeader><TableBody>
                            {filteredEntradas.length > 0 ? filteredEntradas.map(r => (
                                <TableRow key={r.id} className="table-row">
                                    <TableCell className="table-cell">{r.fecha} {r.hora}</TableCell>
                                    <TableCell className="table-cell">{getProveedorNombre(r.proveedor_id)}</TableCell>
                                    <TableCell className="table-cell">{r.tipo_tela}</TableCell>
                                    <TableCell className="table-cell">{r.color}</TableCell>
                                    <TableCell className="table-cell"><span className="text-emerald-400 font-medium">+{r.cantidad_metros}m</span></TableCell>
                                    <TableCell className="table-cell">{r.entrega_nombre}</TableCell>
                                    <TableCell className="table-cell">{r.recibe_nombre}</TableCell>
                                </TableRow>
                            )) : (
                                <TableRow><TableCell colSpan={7} className="text-center py-8 text-zinc-400">No hay entradas registradas</TableCell></TableRow>
                            )}
                        </TableBody></Table>
                    </div></CardContent></Card>
                </TabsContent>

                <TabsContent value="salidas">
                    <Card className="card"><CardContent className="p-0"><div className="overflow-x-auto">
                        <Table><TableHeader><TableRow className="bg-zinc-800">
                            <TableHead className="table-header">Fecha</TableHead>
                            <TableHead className="table-header">Corte</TableHead>
                            <TableHead className="table-header">Tipo Tela</TableHead>
                            <TableHead className="table-header">Color</TableHead>
                            <TableHead className="table-header">Metros</TableHead>
                            <TableHead className="table-header">Stock Antes</TableHead>
                            <TableHead className="table-header">Stock Desp.</TableHead>
                            <TableHead className="table-header">Entrega</TableHead>
                            <TableHead className="table-header">Recibe</TableHead>
                        </TableRow></TableHeader><TableBody>
                            {filteredSalidas.length > 0 ? filteredSalidas.map(r => (
                                <TableRow key={r.id} className="table-row">
                                    <TableCell className="table-cell">{r.fecha} {r.hora}</TableCell>
                                    <TableCell className="table-cell">
                                        {r.corte_relacion ? (
                                            <span className="badge-info">{r.corte_relacion}</span>
                                        ) : r.destino || '-'}
                                    </TableCell>
                                    <TableCell className="table-cell">{r.tipo_tela}</TableCell>
                                    <TableCell className="table-cell">{r.color}</TableCell>
                                    <TableCell className="table-cell"><span className="text-red-400 font-medium">-{r.cantidad_metros?.toFixed?.(2) ?? r.cantidad_metros}m</span></TableCell>
                                    <TableCell className="table-cell">{r.stock_antes != null ? `${r.stock_antes}m` : '-'}</TableCell>
                                    <TableCell className="table-cell">{r.stock_despues != null ? `${r.stock_despues}m` : '-'}</TableCell>
                                    <TableCell className="table-cell">{r.entrega_nombre}</TableCell>
                                    <TableCell className="table-cell">{r.recibe_nombre}</TableCell>
                                </TableRow>
                            )) : (
                                <TableRow><TableCell colSpan={9} className="text-center py-8 text-zinc-400">No hay salidas registradas</TableCell></TableRow>
                            )}
                        </TableBody></Table>
                    </div></CardContent></Card>
                </TabsContent>
            </Tabs>

            {/* ── Entrada Modal ── */}
            <Dialog open={entradaModalOpen} onOpenChange={setEntradaModalOpen}>
                <DialogContent className="sm:max-w-[600px]" data-testid="entrada-tela-modal">
                    <DialogHeader>
                        <DialogTitle><ArrowDownCircle className="w-5 h-5 inline mr-2 text-emerald-400" />Nueva Entrada de Tela</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleEntradaSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group"><Label className="form-label">Fecha *</Label>
                                <Input type="date" value={entradaForm.fecha} onChange={e => setEntradaForm({ ...entradaForm, fecha: e.target.value })} />
                            </div>
                            <div className="form-group"><Label className="form-label">Hora *</Label>
                                <Input type="time" value={entradaForm.hora} onChange={e => setEntradaForm({ ...entradaForm, hora: e.target.value })} />
                            </div>
                        </div>

                        <div className="form-group"><Label className="form-label">Proveedor *</Label>
                            <Select value={entradaForm.proveedor_id} onValueChange={v => setEntradaForm({ ...entradaForm, proveedor_id: v })}>
                                <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                                <SelectContent>{proveedores.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div className="form-group"><Label className="form-label">Tipo de Tela *</Label>
                                <Input value={entradaForm.tipo_tela} onChange={e => setEntradaForm({ ...entradaForm, tipo_tela: e.target.value })} placeholder="Mezclilla, etc." />
                            </div>
                            <div className="form-group"><Label className="form-label">Color *</Label>
                                <Input value={entradaForm.color} onChange={e => setEntradaForm({ ...entradaForm, color: e.target.value })} placeholder="Azul, Negro, etc." />
                            </div>
                            <div className="form-group"><Label className="form-label">Cantidad (metros) *</Label>
                                <Input type="number" step="0.01" value={entradaForm.cantidad_metros} onChange={e => setEntradaForm({ ...entradaForm, cantidad_metros: parseFloat(e.target.value) || 0 })} />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group"><Label className="form-label">Costo Total</Label>
                                <Input type="number" step="0.01" value={entradaForm.costo_total} onChange={e => setEntradaForm({ ...entradaForm, costo_total: parseFloat(e.target.value) || 0 })} placeholder="$0.00" />
                                {entradaForm.costo_total > 0 && entradaForm.proveedor_id && (
                                    <p className="text-xs text-red-400 mt-1">Se sumara a la deuda del proveedor</p>
                                )}
                            </div>
                            <div className="form-group"><Label className="form-label">Lote (opcional)</Label>
                                <Select value={entradaForm.lote_id} onValueChange={v => setEntradaForm({ ...entradaForm, lote_id: v })}>
                                    <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                                    <SelectContent>{lotes.map(l => <SelectItem key={l.id} value={l.id}>{l.numero_lote}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group"><Label className="form-label">Quien Entrega *</Label>
                                <Input value={entradaForm.entrega_nombre} onChange={e => setEntradaForm({ ...entradaForm, entrega_nombre: e.target.value })} placeholder="Nombre" />
                            </div>
                            <div className="form-group"><Label className="form-label">Quien Recibe *</Label>
                                <Input value={entradaForm.recibe_nombre} onChange={e => setEntradaForm({ ...entradaForm, recibe_nombre: e.target.value })} placeholder="Nombre" />
                            </div>
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setEntradaModalOpen(false)}>Cancelar</Button>
                            <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600" data-testid="submit-entrada">Registrar Entrada</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* ══════════ SALIDA PRODUCCION MODAL ══════════ */}
            <Dialog open={salidaModalOpen} onOpenChange={(open) => { setSalidaModalOpen(open); if (!open) setConfirmando(false); }}>
                <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto" data-testid="salida-tela-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ArrowUpCircle className="w-5 h-5 text-red-400" />
                            Salida de Tela — Produccion
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-5">
                        {/* SECTION 1: Relacion con Produccion */}
                        <div className="space-y-3">
                            <h3 className="text-sm font-semibold text-amber-400 flex items-center gap-2">
                                <Scissors className="w-4 h-4" /> 1. Relacion con Produccion
                            </h3>
                            <div className="form-group">
                                <Label className="form-label">Numero de Corte *</Label>
                                <Select value={salidaForm.corte_id} onValueChange={v => setSalidaForm({ ...salidaForm, corte_id: v })}>
                                    <SelectTrigger data-testid="select-corte"><SelectValue placeholder="Seleccionar corte existente" /></SelectTrigger>
                                    <SelectContent>
                                        {cortes.map(c => (
                                            <SelectItem key={c.id} value={c.id}>
                                                {c.relacion_corte} — {c.marca} {c.modelo} ({c.cantidad_total} pzas)
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            {corteSeleccionado && (
                                <div className="p-3 rounded-lg bg-zinc-800/70 border border-zinc-700 text-sm space-y-1" data-testid="corte-info">
                                    <div className="flex justify-between">
                                        <span className="text-zinc-400">Corte:</span>
                                        <span className="text-zinc-100 font-medium">{corteSeleccionado.relacion_corte}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-zinc-400">Marca / Modelo:</span>
                                        <span className="text-zinc-100">{corteSeleccionado.marca} {corteSeleccionado.modelo}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-zinc-400">Piezas:</span>
                                        <span className="text-amber-400 font-medium">{corteSeleccionado.cantidad_total}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-zinc-400">Cortador:</span>
                                        <span className="text-zinc-100">{corteSeleccionado.cortador_nombre}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* SECTION 2: Captura Operativa */}
                        <div className="space-y-3">
                            <h3 className="text-sm font-semibold text-amber-400 flex items-center gap-2">
                                <Package className="w-4 h-4" /> 2. Captura Operativa
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group"><Label className="form-label">Cantidad de cortes *</Label>
                                    <Input type="number" min="1" value={salidaForm.cantidad_cortes} onChange={e => setSalidaForm({ ...salidaForm, cantidad_cortes: e.target.value })} placeholder="Ej: 5" data-testid="input-cantidad-cortes" />
                                </div>
                                <div className="form-group"><Label className="form-label">Promedio metros/corte *</Label>
                                    <Input type="number" step="0.01" min="0.01" value={salidaForm.promedio_metros_por_corte} onChange={e => setSalidaForm({ ...salidaForm, promedio_metros_por_corte: e.target.value })} placeholder="Ej: 1.5" data-testid="input-promedio-metros" />
                                </div>
                            </div>
                            {metrosSalida > 0 && (
                                <div className="p-3 rounded-lg bg-amber-400/10 border border-amber-400/30 text-center" data-testid="metros-calculo">
                                    <p className="text-xs text-zinc-400">Metros a descontar</p>
                                    <p className="text-2xl font-bold text-amber-400">{metrosSalida.toFixed(2)} m</p>
                                    <p className="text-xs text-zinc-500">{salidaForm.cantidad_cortes} cortes x {salidaForm.promedio_metros_por_corte} m/corte</p>
                                </div>
                            )}
                        </div>

                        {/* SECTION 3: Inventario */}
                        <div className="space-y-3">
                            <h3 className="text-sm font-semibold text-amber-400 flex items-center gap-2">
                                <Shirt className="w-4 h-4" /> 3. Seleccion de Inventario
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group"><Label className="form-label">Tipo de Tela *</Label>
                                    <Select value={salidaForm.tipo_tela} onValueChange={v => setSalidaForm({ ...salidaForm, tipo_tela: v, color: '' })}>
                                        <SelectTrigger data-testid="select-tipo-tela-salida"><SelectValue placeholder="Seleccionar tipo" /></SelectTrigger>
                                        <SelectContent>
                                            {tiposTelaDisponibles.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="form-group"><Label className="form-label">Color *</Label>
                                    <Select value={salidaForm.color} onValueChange={v => setSalidaForm({ ...salidaForm, color: v })} disabled={!salidaForm.tipo_tela}>
                                        <SelectTrigger data-testid="select-color-salida"><SelectValue placeholder={salidaForm.tipo_tela ? "Seleccionar color" : "Primero seleccione tipo"} /></SelectTrigger>
                                        <SelectContent>
                                            {coloresDisponibles.map(c => (
                                                <SelectItem key={c.color} value={c.color}>{c.color} ({c.stock.toFixed(1)}m disp.)</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Stock Display */}
                            {salidaForm.tipo_tela && salidaForm.color && stockSeleccionado !== null && (
                                <div className={`p-3 rounded-lg border ${
                                    metrosSalida > 0
                                        ? stockSuficiente
                                            ? 'bg-emerald-400/5 border-emerald-400/30'
                                            : 'bg-red-400/5 border-red-400/30'
                                        : 'bg-zinc-800/50 border-zinc-700'
                                }`} data-testid="stock-display">
                                    <div className="grid grid-cols-3 gap-4 text-center">
                                        <div>
                                            <p className="text-xs text-zinc-400">Stock Actual</p>
                                            <p className="text-lg font-bold text-zinc-100">{stockSeleccionado.toFixed(2)} m</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-zinc-400">Descontar</p>
                                            <p className="text-lg font-bold text-red-400">-{metrosSalida.toFixed(2)} m</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-zinc-400">Quedara</p>
                                            <p className={`text-lg font-bold ${stockSuficiente ? 'text-emerald-400' : 'text-red-400'}`}>
                                                {stockDespues !== null ? `${stockDespues.toFixed(2)} m` : '-'}
                                            </p>
                                        </div>
                                    </div>
                                    {!stockSuficiente && metrosSalida > 0 && (
                                        <div className="flex items-center gap-2 mt-2 text-red-400 text-xs justify-center">
                                            <AlertTriangle className="w-4 h-4" />
                                            <span>Stock insuficiente para esta salida</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* SECTION 4: Fecha y personal */}
                        <div className="space-y-3">
                            <h3 className="text-sm font-semibold text-amber-400 flex items-center gap-2">
                                <Info className="w-4 h-4" /> 4. Datos de la Salida
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group"><Label className="form-label">Fecha *</Label>
                                    <Input type="date" value={salidaForm.fecha} onChange={e => setSalidaForm({ ...salidaForm, fecha: e.target.value })} />
                                </div>
                                <div className="form-group"><Label className="form-label">Hora *</Label>
                                    <Input type="time" value={salidaForm.hora} onChange={e => setSalidaForm({ ...salidaForm, hora: e.target.value })} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group"><Label className="form-label">Quien Entrega *</Label>
                                    <Input value={salidaForm.entrega_nombre} onChange={e => setSalidaForm({ ...salidaForm, entrega_nombre: e.target.value })} placeholder="Nombre" data-testid="input-entrega-salida" />
                                </div>
                                <div className="form-group"><Label className="form-label">Quien Recibe *</Label>
                                    <Input value={salidaForm.recibe_nombre} onChange={e => setSalidaForm({ ...salidaForm, recibe_nombre: e.target.value })} placeholder="Nombre" data-testid="input-recibe-salida" />
                                </div>
                            </div>
                            <div className="form-group"><Label className="form-label">Observaciones</Label>
                                <Input value={salidaForm.observaciones} onChange={e => setSalidaForm({ ...salidaForm, observaciones: e.target.value })} placeholder="Notas adicionales" />
                            </div>
                        </div>

                        {/* Confirmation */}
                        {confirmando ? (
                            <div className="p-4 rounded-lg bg-amber-400/10 border-2 border-amber-400/50 space-y-3" data-testid="confirmacion-salida">
                                <h4 className="font-semibold text-amber-400 text-center">Confirmar Salida</h4>
                                <div className="text-sm space-y-1">
                                    <div className="flex justify-between"><span className="text-zinc-400">Corte:</span><span className="text-zinc-100">{corteSeleccionado?.relacion_corte}</span></div>
                                    <div className="flex justify-between"><span className="text-zinc-400">Tela:</span><span className="text-zinc-100">{salidaForm.tipo_tela} - {salidaForm.color}</span></div>
                                    <div className="flex justify-between"><span className="text-zinc-400">Metros:</span><span className="text-red-400 font-bold">{metrosSalida.toFixed(2)} m</span></div>
                                    <div className="flex justify-between"><span className="text-zinc-400">Stock:</span><span className="text-zinc-100">{stockSeleccionado?.toFixed(2)}m → {stockDespues?.toFixed(2)}m</span></div>
                                </div>
                                <div className="flex gap-2 pt-2">
                                    <Button type="button" variant="outline" className="flex-1" onClick={() => setConfirmando(false)}>Volver</Button>
                                    <Button type="button" className="flex-1 bg-red-500 hover:bg-red-600" onClick={handleSalidaSubmit} data-testid="confirm-salida-btn">
                                        <CheckCircle2 className="w-4 h-4 mr-2" /> Confirmar Salida
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setSalidaModalOpen(false)}>Cancelar</Button>
                                <Button type="button" className="bg-red-500 hover:bg-red-600"
                                    disabled={!salidaForm.corte_id || !salidaForm.tipo_tela || !salidaForm.color || !metrosSalida || !stockSuficiente || !salidaForm.entrega_nombre || !salidaForm.recibe_nombre}
                                    onClick={() => setConfirmando(true)} data-testid="review-salida-btn">
                                    Revisar Salida
                                </Button>
                            </DialogFooter>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};
